import type {
  ForecasterSpec,
  ImbalanceForecastMetricsReport,
  PerMinuteForecasterVersion,
  PlannerSetupSpec,
  SetupValueSummary,
  StepForecasterVersion,
  ValueGroup,
} from "../../../services/client";

// Everything statistical (sums, intervals, per MW-year) comes from the
// backend; these helpers only pick rows out of the report and name them.

export const CYCLING_COSTS = [0, 30, 50] as const;
export type CyclingCost = (typeof CYCLING_COSTS)[number];

export type MetricsSource = "live" | "research";
export type ErrorMetric = "mae" | "rmse";

export const ERROR_METRIC_LABELS: Record<ErrorMetric, string> = {
  mae: "MAE",
  rmse: "RMSE",
};

// A categorical palette that stays apart for up to eight setups; the
// baseline is always grey and the ceiling always green.
const PALETTE = [
  "#d46b08",
  "#722ed1",
  "#0958d9",
  "#c41d7f",
  "#08979c",
  "#262626",
  "#ad6800",
  "#531dab",
];
export const BASELINE_COLOR = "#8c8c8c";
export const CEILING_COLOR = "#389e0d";

export const setupColor = (
  setups: PlannerSetupSpec[],
  name: string,
): string => {
  const setup = setups.find((candidate) => candidate.name === name);
  if (setup?.role === "baseline") {
    return BASELINE_COLOR;
  }
  if (setup?.role === "ceiling") {
    return CEILING_COLOR;
  }
  const others = setups.filter(
    (candidate) =>
      candidate.role !== "baseline" && candidate.role !== "ceiling",
  );
  const index = others.findIndex((candidate) => candidate.name === name);
  return PALETTE[Math.max(index, 0) % PALETTE.length];
};

export const forecasterColor = (
  forecasters: ForecasterSpec[],
  name: string,
): string => {
  if (name === "elia_live") {
    return BASELINE_COLOR;
  }
  const index = forecasters
    .filter((forecaster) => forecaster.name !== "elia_live")
    .findIndex((forecaster) => forecaster.name === name);
  return PALETTE[Math.max(index, 0) % PALETTE.length];
};

export const labelOf = (
  specs: { name: string; label: string }[],
  name: string,
): string => specs.find((spec) => spec.name === name)?.label ?? name;

export const defaultSetups = (setups: PlannerSetupSpec[]): string[] =>
  setups
    .filter((setup) => setup.role !== "candidate")
    .map((setup) => setup.name);

export const PER_MINUTE_VERSION_LABELS: Record<
  PerMinuteForecasterVersion,
  string
> = {
  median_only: "median only",
  with_expected_price: "with expected_price",
  mixed: "release day (mixed)",
  none: "no forecasts",
  research: "research replay",
};

export const STEP_VERSION_LABELS: Record<StepForecasterVersion, string> = {
  pooled: "pooled multi-step model",
  per_step: "per-step models 30-120 min",
  mixed: "release day (mixed)",
  none: "no forecasts",
  research: "research replay",
};

export const groupVersionLabel = (group: ValueGroup) =>
  group.per_minute_version === "research"
    ? "research replay of the stored forecasts"
    : `per-minute ${PER_MINUTE_VERSION_LABELS[group.per_minute_version]}, 15-min ${STEP_VERSION_LABELS[group.step_version]}`;

export const groupAt = (
  groups: ValueGroup[],
  cost: CyclingCost,
): ValueGroup[] => groups.filter((group) => group.cycling_cost === cost);

export const setupIn = (
  group: ValueGroup | undefined,
  setup: string,
): SetupValueSummary | undefined =>
  group?.setups.find((summary) => summary.setup === setup);

// The interval is over the group's days in euros; per MW-year it scales with
// the same factor as the net revenue.
export const perMwYear = (euros: number, days: number) =>
  days === 0 ? 0 : (euros / days) * 365.25;

export const hasDays = (report: ImbalanceForecastMetricsReport | undefined) =>
  (report?.days.length ?? 0) > 0;

export const euros = (value: number) =>
  value.toLocaleString("en-GB", { maximumFractionDigits: 0 });

export const signedEuros = (value: number) =>
  `${value >= 0 ? "+" : ""}${euros(value)}`;

export const percent = (value: number | null | undefined, digits = 1) =>
  value === null || value === undefined
    ? "–"
    : `${(value * 100).toFixed(digits)}%`;
